import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from 'antd';
import type { MenuProps } from 'antd';
import { SectionNavContainer, StyledSectionNav } from './index.styled';

export type SectionNavItem = {
  key: string;
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};

export type SectionNavGroup = {
  key: string;
  label: React.ReactNode;
  children: SectionNavItem[];
  badgeCount?: number;
  disabled?: boolean;
};

export type AppSectionNavProps = {
  items: Array<SectionNavItem | SectionNavGroup>;
  activeKey?: string;
  defaultOpenKeys?: string[];
  openKeys?: string[];
  onOpenKeysChange?: (openKeys: string[]) => void;
  className?: string;
};

const isGroup = (item: SectionNavItem | SectionNavGroup): item is SectionNavGroup =>
  (item as SectionNavGroup).children !== undefined;

const AppSectionNav: React.FC<AppSectionNavProps> = ({
  items,
  activeKey,
  defaultOpenKeys,
  openKeys: controlledOpenKeys,
  onOpenKeysChange,
  className,
}) => {
  const derivedDefaultOpenKeys = useMemo(() => {
    if (defaultOpenKeys?.length) return defaultOpenKeys;
    if (!activeKey) return [];
    const containingGroup = items.find(
      (it) => isGroup(it) && it.children.some((c) => c.key === activeKey),
    ) as SectionNavGroup | undefined;
    return containingGroup ? [containingGroup.key] : [];
  }, [activeKey, defaultOpenKeys, items]);

  const [uncontrolledOpenKeys, setUncontrolledOpenKeys] = useState<string[]>(
    derivedDefaultOpenKeys,
  );

  useEffect(() => {
    if (controlledOpenKeys) return;
    setUncontrolledOpenKeys(derivedDefaultOpenKeys);
  }, [controlledOpenKeys, derivedDefaultOpenKeys]);

  const openKeys = controlledOpenKeys ?? uncontrolledOpenKeys;

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    // Keep it simple: only one group open at a time (same as app sidebar)
    const nextKeys = Array.isArray(keys) ? keys : [];
    const latestOpenKey = nextKeys.find((k) => !openKeys.includes(k));
    const single = latestOpenKey ? [latestOpenKey] : [];

    if (!controlledOpenKeys) setUncontrolledOpenKeys(single);
    onOpenKeysChange?.(single);
  };

  const menuItems: MenuProps['items'] = useMemo(() => {
    return items.map((item) => {
      if (!isGroup(item)) {
        return {
          key: item.key,
          label: item.label,
          disabled: item.disabled,
          onClick: item.onClick,
        } as any;
      }

      const badgeCount = item.badgeCount ?? item.children.length;
      return {
        key: item.key,
        disabled: item.disabled,
        label: (
          <div className="section-nav-submenu-title">
            <div className="section-nav-submenu-left">
              <span className="section-nav-submenu-label">{item.label}</span>
            </div>
            <Badge count={badgeCount} />
          </div>
        ),
        children: item.children.map((c) => ({
          key: c.key,
          label: c.label,
          disabled: c.disabled,
          onClick: c.onClick,
        })),
      } as any;
    });
  }, [items]);

  return (
    <SectionNavContainer className={className}>
      <StyledSectionNav
        mode="inline"
        selectable
        selectedKeys={activeKey ? [activeKey] : []}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
      />
    </SectionNavContainer>
  );
};

export default AppSectionNav;

